import { appTileLabel } from './coverageHelpers';
import { COVERAGE_BAR_TONE_CLASS, coverageBarTone } from './coverageMatrixHelpers';
import shared from './coverageShared.module.css';
import styles from './CoverageRail.module.css';

/**
 * EAA-1817: the three small nodes both coverage rails hand to
 * `CoverageRailShell` — the leading code tile, the coverage bar, and the
 * right-hand defect badge + ratio (frame 55, left column).
 *
 * They are components rather than three copies of the same JSX in two rails
 * because the bar in particular carries a rule (`coverageBarTone`'s thresholds)
 * that must read the same on both axes and in the matrix's row headers. A
 * "50% is green" that lives in three places is a "50% is green" that becomes
 * three different numbers.
 */

export function RailCodeTile({ code }: { code: string }) {
    return (
        <span className={shared.appTile} aria-hidden="true">
            {appTileLabel(code)}
        </span>
    );
}

export function CoverageBar({ covered, total }: { covered: number; total: number }) {
    const tone = coverageBarTone(covered, total);
    const percent = total > 0 ? Math.round((covered / total) * 100) : 0;
    return (
        <span className={shared.progressTrack} aria-hidden="true">
            <span
                className={`${shared.progressFill} ${COVERAGE_BAR_TONE_CLASS[tone]}`}
                style={{ width: `${percent}%` }}
            />
        </span>
    );
}

/**
 * The red count badge (frame 55 draws it only when there IS a defect) and the
 * `n/m` ratio. `aria-hidden`: the row's own `ariaLabel` already states both in
 * a sentence, so reading these fragments again would double every row.
 */
export function RailDefectBadge({
    defectCount,
    covered,
    total,
}: {
    defectCount: number;
    covered: number;
    total: number;
}) {
    return (
        <span className={styles.appRowMeta} aria-hidden="true">
            {defectCount > 0 && <span className={styles.appRowBadge}>{defectCount}</span>}
            <span className={styles.appRowRatio}>
                {covered}/{total}
            </span>
        </span>
    );
}
